import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Typography, 
  Container, 
  Box, 
  Alert, 
  Link,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { useUser } from '../contexts/UserContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [twoFactorCode, setTwoFactorCode] = useState<string>('');
  const [showTwoFactor, setShowTwoFactor] = useState<boolean>(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const navigate = useNavigate();
  const { login, verifyTwoFactor, requestPasswordReset } = useUser();

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    try {
      if (!showTwoFactor) {
        await login(email, password);
        // setShowTwoFactor(true);
        navigate('/');
      } else {
        await verifyTwoFactor(twoFactorCode);
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred during login');
    }
  };

  const handleForgotPassword = async () => {
    try {
      await requestPasswordReset(resetEmail);
      setOpenForgotPassword(false);
      // You might want to show a success message here
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred during password reset');
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        component="form"
        onSubmit={handleLogin}
        sx={{ 
          mt: 8, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          padding: 3,
        }}
      >
        <Box>
          <Box sx={{ height: 125 }}>
            <img src="/logo.svg" alt="Logo" style={{ height: '100%' }} />
          </Box>
          <Box sx={{ height: 30, textAlign: 'center', margin: '0px', mb: 3, }}>
            <img src="/theRoofingApp.svg" alt="The Roofing App" style={{ height: '100%' }} />
          </Box>
        </Box>

        <Typography component="h1" variant="h5" gutterBottom>
          {showTwoFactor ? 'Two-Factor Authentication' : 'Login'}
        </Typography>
        {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
        {!showTwoFactor ? (
          <>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </>
        ) : (
          <TextField
            margin="normal"
            required
            fullWidth
            id="twoFactorCode"
            label="Two-Factor Code"
            name="twoFactorCode"
            autoComplete="one-time-code"
            autoFocus
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value)}
          />
        )}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
        >
          {showTwoFactor ? 'Verify' : 'Login'}
        </Button>
        {!showTwoFactor && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Link href="#" variant="body2" onClick={() => setOpenForgotPassword(true)}>
              Forgot password?
            </Link>
            <Link href="/register" variant="body2">
              Create new account
            </Link>
          </Box>
        )}
      </Box>

      <Dialog open={openForgotPassword} onClose={() => setOpenForgotPassword(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To reset your password, please enter your email address here. We will send you instructions to reset your password.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="resetEmail"
            label="Email Address"
            type="email"
            fullWidth
            variant="standard"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForgotPassword(false)}>Cancel</Button>
          <Button onClick={handleForgotPassword}>Reset Password</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;